import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import GroundMaterial from '../Materials/Ground.js'
import ShadowMaterial from '../Materials/Shadow.js'
import { ARENA } from '../Game/definitions.js'

export default class ModelFactory
{
    constructor(_options)
    {
        this.resources = _options.resources
        this.materials = _options.materials.shades.items
        for(const material of Object.values(this.materials))
            material.uniforms.uRevealProgress.value = 1
        this.addMaterial('bone', 'white', '#e7dfc9')
        this.addMaterial('deadwood', 'brown', '#4f3c3e')
        this.addMaterial('graveStone', 'gray', '#292b35')
        this.addMaterial('graveIron', 'metal', '#718583')
        this.addMaterial('soul', 'emeraldGreen', '#63ffa6')
        this.addMaterial('ectoplasm', 'green', '#25b977')
        this.addMaterial('soulCrystal', 'emeraldGreen', '#a2ffd2')
        this.addMaterial('robe', 'purple', '#25182f')
        this.addMaterial('robeTrim', 'purple', '#56365f')
        this.glowMaterial = new THREE.MeshBasicMaterial({ color: '#62ff9e', transparent: true, opacity: 0.72, blending: THREE.AdditiveBlending, depthWrite: false })
        this.enemyGlowMaterial = new THREE.MeshBasicMaterial({ color: '#ff6f72', transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false })
    }

    addMaterial(_name, _base, _color)
    {
        const material = this.materials[_base].clone()
        material.name = `shade${_name}`
        material.uniforms.diffuse.value.set(_color)
        this.materials[_name] = material
    }

    mesh(_geometry, _material, _position = [0, 0, 0], _rotation = [0, 0, 0])
    {
        const mesh = new THREE.Mesh(_geometry, _material)
        mesh.position.set(..._position)
        mesh.rotation.set(..._rotation)
        return mesh
    }

    box(_width, _height, _depth, _radius = 0.04, _segments = 2)
    {
        return new RoundedBoxGeometry(_width, _height, _depth, _segments, Math.min(_radius, _width * 0.25, _height * 0.25, _depth * 0.25))
    }

    cylinder(_top, _bottom, _height, _segments = 10)
    {
        const geometry = new THREE.CylinderGeometry(_top, _bottom, _height, _segments)
        geometry.rotateX(Math.PI * 0.5)
        return geometry
    }

    createShadow(_width = 2, _height = _width)
    {
        const material = new ShadowMaterial()
        material.uniforms.uColor.value = new THREE.Color('#120d20')
        material.uniforms.uAlpha.value = 0.52
        material.uniforms.uFadeRadius.value = 0.35
        const shadow = this.mesh(new THREE.PlaneGeometry(_width, _height), material, [0.25, 0.3, 0.025])
        shadow.renderOrder = 1
        return shadow
    }

    createGround()
    {
        return this.mesh(new THREE.PlaneGeometry(2, 2), new GroundMaterial())
    }

    createSkull(_scale = 1, _enemy = false)
    {
        const group = new THREE.Group()
        const cranium = this.mesh(new THREE.SphereGeometry(0.28, 12, 9), this.materials.bone, [0, 0, 0.28])
        cranium.scale.set(0.9, 0.78, 1)
        group.add(cranium)
        group.add(this.mesh(this.box(0.32, 0.25, 0.18, 0.035), this.materials.bone, [0, 0.035, 0.08]))
        const eyeMaterial = _enemy ? this.enemyGlowMaterial : this.materials.graveStone
        for(const side of [-1, 1])
        {
            const eye = this.mesh(new THREE.SphereGeometry(0.065, 8, 6), eyeMaterial, [side * 0.105, 0.205, 0.3])
            eye.scale.set(1, 0.35, 0.85)
            group.add(eye)
        }
        group.add(this.mesh(new THREE.ConeGeometry(0.045, 0.09, 3), this.materials.graveStone, [0, 0.225, 0.17], [Math.PI * 0.5, 0, 0]))
        for(const side of [-1, 0, 1])
            group.add(this.mesh(this.box(0.055, 0.055, 0.08, 0.01), this.materials.bone, [side * 0.075, 0.18, 0.025]))
        group.scale.setScalar(_scale)
        return group
    }

    createItem(_resource, _scale = 1)
    {
        const group = new THREE.Group()
        if(_resource === 'bones')
        {
            const skull = this.createSkull(0.9)
            skull.rotation.x = -0.2
            group.add(skull)
        }
        else if(_resource === 'deadwood')
        {
            for(const side of [-1, 1])
            {
                const branch = this.mesh(this.cylinder(0.07, 0.1, 0.55, 7), this.materials.deadwood, [side * 0.1, 0, 0.2], [0.2, side * 0.35, side * 0.25])
                group.add(branch)
            }
            group.add(this.mesh(this.cylinder(0.035, 0.055, 0.35, 6), this.materials.deadwood, [0.2, 0, 0.35], [0.4, -0.7, 0]))
        }
        else if(_resource === 'souls')
        {
            const flame = this.mesh(new THREE.OctahedronGeometry(0.22, 0), this.materials.soul, [0, 0, 0.28])
            flame.scale.z = 1.5
            const glow = this.mesh(new THREE.SphereGeometry(0.3, 10, 8), this.glowMaterial, [0, 0, 0.28])
            group.add(flame, glow)
            group.userData.glow = glow
        }
        else if(_resource === 'graveOre')
        {
            group.add(this.mesh(new THREE.DodecahedronGeometry(0.28, 0), this.materials.graveStone, [0, 0, 0.22], [0.2, 0.1, 0.3]))
            for(const side of [-1, 1])
                group.add(this.mesh(new THREE.OctahedronGeometry(0.08, 0), this.materials.graveIron, [side * 0.17, 0.12, 0.28]))
        }
        else if(_resource === 'ectoplasm')
        {
            for(const [x, y, size] of [[-0.12, 0, 0.18], [0.12, 0.05, 0.15], [0, -0.1, 0.13]])
                group.add(this.mesh(new THREE.SphereGeometry(size, 10, 7), this.materials.ectoplasm, [x, y, size]))
            group.add(this.mesh(new THREE.SphereGeometry(0.28, 10, 8), this.glowMaterial, [0, 0, 0.18]))
        }
        else if(_resource === 'graveIron')
        {
            group.add(this.mesh(this.box(0.48, 0.28, 0.18, 0.04), this.materials.graveIron, [0, 0, 0.1]))
            group.add(this.mesh(this.box(0.22, 0.025, 0.025, 0.005), this.glowMaterial, [0, 0.145, 0.13], [0, 0, 0.5]))
        }
        else if(_resource === 'soulCrystal')
        {
            const crystal = this.mesh(new THREE.OctahedronGeometry(0.26, 0), this.materials.soulCrystal, [0, 0, 0.3])
            crystal.scale.z = 1.45
            group.add(crystal)
        }
        else if(_resource === 'graveSeal')
        {
            group.add(this.mesh(this.cylinder(0.28, 0.28, 0.09, 12), this.materials.graveIron, [0, 0, 0.08]))
            group.add(this.mesh(new THREE.TorusGeometry(0.19, 0.025, 5, 12), this.glowMaterial, [0, 0, 0.14]))
            for(let index = 0; index < 4; index++)
            {
                const angle = index * Math.PI * 0.5
                group.add(this.mesh(this.box(0.06, 0.025, 0.025, 0.005), this.materials.bone, [Math.cos(angle) * 0.2, Math.sin(angle) * 0.2, 0.15], [0, 0, angle]))
            }
        }
        else if(_resource === 'spiritBinding')
        {
            for(let index = 0; index < 3; index++)
            {
                const link = this.mesh(new THREE.TorusGeometry(0.14, 0.035, 6, 12), this.materials.soul, [(index - 1) * 0.18, 0, 0.18 + index * 0.04], [index % 2 ? Math.PI * 0.5 : 0, 0, 0])
                group.add(link)
            }
        }
        group.scale.setScalar(_scale)
        return group
    }

    createTree()
    {
        const group = new THREE.Group()
        const trunk = this.mesh(this.cylinder(0.12, 0.24, 2.3, 7), this.materials.deadwood, [0, 0, 1.15], [0.08, 0.14, 0])
        group.add(trunk)
        for(const side of [-1, 1])
        {
            group.add(this.mesh(this.cylinder(0.04, 0.1, 1.4, 6), this.materials.deadwood, [side * 0.38, 0, 2.1], [0, side * 0.65, 0]))
            group.add(this.mesh(this.cylinder(0.025, 0.055, 0.85, 6), this.materials.deadwood, [side * 0.78, 0.05, 2.55], [0.2, side * 0.85, 0]))
        }
        return group
    }

    createDeposit(_deposit)
    {
        const group = new THREE.Group()
        group.position.set(_deposit.x, _deposit.y, 0)
        if(_deposit.tree)
        {
            const tree = this.createTree()
            const stump = this.mesh(this.cylinder(0.15, 0.23, 0.32, 7), this.materials.deadwood, [0, 0, 0.16])
            stump.visible = false
            group.add(tree, stump)
            group.userData.tree = tree
            group.userData.stump = stump
        }
        else
        {
            const material = _deposit.resource === 'bones' ? this.materials.bone : _deposit.resource === 'souls' || _deposit.resource === 'ectoplasm' ? this.materials.ectoplasm : this.materials.graveStone
            for(let index = 0; index < 20; index++)
            {
                const angle = index * 2.399963
                const radius = index === 0 ? 0 : 0.5 + (index % 6) * 0.28
                const scale = 0.35 + (index % 4) * 0.1
                const rock = this.mesh(new THREE.DodecahedronGeometry(0.65, 0), material, [Math.cos(angle) * radius, Math.sin(angle) * radius, 0.2], [0.1, 0.2, angle])
                rock.scale.set(scale, scale * 0.8, scale * 0.65)
                group.add(rock)
                if(_deposit.resource === 'bones' && index % 4 === 0)
                {
                    const skull = this.createSkull(0.85)
                    skull.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0.35)
                    skull.rotation.z = angle
                    group.add(skull)
                }
                if(_deposit.resource === 'graveOre' && index % 3 === 0)
                    group.add(this.mesh(new THREE.OctahedronGeometry(0.18, 0), this.materials.soul, [Math.cos(angle) * radius, Math.sin(angle) * radius, 0.55]))
            }
            if(_deposit.resource === 'souls' || _deposit.resource === 'ectoplasm')
            {
                for(let index = 0; index < 5; index++)
                {
                    const wisp = this.createItem('souls', 1.3)
                    wisp.position.set(Math.cos(index * 1.25) * 1.1, Math.sin(index * 1.25) * 1.1, 0.35 + (index % 2) * 0.3)
                    group.add(wisp)
                }
            }
        }
        const output = new THREE.Group()
        output.position.set(0, -2.5, 0.1)
        group.add(output)
        group.userData.output = output
        return group
    }

    createNecromancer()
    {
        const group = new THREE.Group()
        const body = new THREE.Group()
        group.add(body)
        body.add(this.mesh(new THREE.ConeGeometry(0.52, 1.15, 10), this.materials.robe, [0, 0, 0.78], [Math.PI * 0.5, 0, 0]))
        body.add(this.mesh(this.cylinder(0.37, 0.42, 0.45, 10), this.materials.robeTrim, [0, 0, 1.15]))
        body.add(this.mesh(new THREE.TorusGeometry(0.37, 0.035, 6, 12), this.glowMaterial, [0, 0, 0.86]))
        const head = new THREE.Group()
        head.position.set(0, 0.03, 1.55)
        const hood = this.mesh(new THREE.SphereGeometry(0.4, 14, 10), this.materials.robe)
        hood.scale.set(1, 0.9, 1.05)
        head.add(hood)
        const mask = this.createSkull(0.78)
        mask.position.set(0, 0.18, -0.22)
        head.add(mask)
        body.add(head)
        const arms = []
        for(const side of [-1, 1])
        {
            const pivot = new THREE.Group()
            pivot.position.set(side * 0.4, 0, 1.2)
            pivot.add(this.mesh(this.cylinder(0.1, 0.15, 0.62, 8), this.materials.robeTrim, [0, 0, -0.25], [0, side * 0.15, 0]))
            pivot.add(this.mesh(new THREE.SphereGeometry(0.1, 10, 8), this.materials.bone, [0, 0, -0.57]))
            body.add(pivot)
            arms.push(pivot)
        }
        const staff = new THREE.Group()
        staff.position.set(0, 0.05, -0.55)
        staff.add(this.mesh(this.cylinder(0.035, 0.05, 1.65, 7), this.materials.deadwood, [0, 0, 0.35]))
        staff.add(this.mesh(new THREE.TorusGeometry(0.18, 0.035, 6, 12), this.materials.graveIron, [0, 0, 1.17]))
        const crystal = this.createItem('soulCrystal', 0.75)
        crystal.position.z = 1.05
        staff.add(crystal)
        arms[1].add(staff)
        const inventory = new THREE.Group()
        inventory.position.set(0, -0.43, 1.05)
        body.add(inventory)
        group.userData = { body, head, arms, legs: [], staff, inventory }
        return group
    }

    createSkeleton(_type, _enemy = false)
    {
        const group = new THREE.Group()
        const body = new THREE.Group()
        group.add(body)
        const scale = _type === 'brute' || _type === 'inquisitor' ? 1.35 : 1
        body.scale.setScalar(scale)
        const eyeMaterial = _enemy ? this.enemyGlowMaterial : this.glowMaterial
        const skull = this.createSkull(0.72, _enemy)
        skull.position.set(0, 0, 1.25)
        body.add(skull)
        body.add(this.mesh(this.cylinder(0.055, 0.055, 0.55, 7), this.materials.bone, [0, 0, 0.82]))
        for(let index = 0; index < 3; index++)
            body.add(this.mesh(this.box(0.48 - index * 0.05, 0.12, 0.06, 0.02), this.materials.bone, [0, 0, 0.92 + index * 0.13]))
        body.add(this.mesh(this.box(0.34, 0.18, 0.12, 0.03), this.materials.bone, [0, 0, 0.5]))
        const arms = []
        const legs = []
        for(const side of [-1, 1])
        {
            const arm = new THREE.Group()
            arm.position.set(side * 0.3, 0, 1.02)
            arm.add(this.mesh(this.cylinder(0.045, 0.05, 0.55, 6), this.materials.bone, [0, 0, -0.25]))
            body.add(arm)
            arms.push(arm)
            const leg = new THREE.Group()
            leg.position.set(side * 0.13, 0, 0.48)
            leg.add(this.mesh(this.cylinder(0.05, 0.06, 0.52, 6), this.materials.bone, [0, 0, -0.25]))
            leg.add(this.mesh(this.box(0.14, 0.28, 0.08, 0.025), this.materials.bone, [0, 0.07, -0.52]))
            body.add(leg)
            legs.push(leg)
        }
        for(const side of [-1, 1])
        {
            const eye = this.mesh(new THREE.SphereGeometry(0.035, 7, 5), eyeMaterial, [side * 0.075, 0.16, 1.47])
            body.add(eye)
        }

        if(_type === 'guard' || _type === 'inquisitor')
        {
            arms[1].add(this.mesh(this.box(0.07, 0.09, 0.85, 0.015), this.materials.graveIron, [0, 0, -0.7]))
            arms[0].add(this.mesh(this.cylinder(0.32, 0.32, 0.08, 10), this.materials.graveStone, [0, 0.08, -0.28], [Math.PI * 0.5, 0, 0]))
        }
        else if(_type === 'miner')
            arms[1].add(this.mesh(this.box(0.65, 0.08, 0.08, 0.015), this.materials.graveIron, [0, 0, -0.58], [0, 0, 0.25]))
        else if(_type === 'woodcutter')
            arms[1].add(this.mesh(this.box(0.12, 0.4, 0.25, 0.025), this.materials.graveIron, [0, 0, -0.58]))
        else if(_type === 'archer')
            arms[0].add(this.mesh(new THREE.TorusGeometry(0.35, 0.025, 5, 16, Math.PI), this.materials.deadwood, [0, 0, -0.35], [0, Math.PI * 0.5, 0]))
        else if(_type === 'brute')
        {
            for(const side of [-1, 1])
                body.add(this.mesh(new THREE.ConeGeometry(0.12, 0.4, 6), this.materials.bone, [side * 0.38, 0, 1.12], [0, side * 0.7, 0]))
        }
        else if(_type === 'lich' || _type === 'wraith')
        {
            body.add(this.mesh(new THREE.ConeGeometry(0.43, 0.95, 9), this.materials.robe, [0, 0, 0.55], [Math.PI * 0.5, 0, 0]))
            arms[1].add(this.mesh(this.cylinder(0.035, 0.05, 1.35, 7), this.materials.deadwood, [0, 0, -0.25]))
            arms[1].add(this.mesh(new THREE.OctahedronGeometry(0.15, 0), eyeMaterial, [0, 0, 0.45]))
        }
        else if(_type === 'porter')
        {
            body.add(this.mesh(this.box(0.58, 0.34, 0.52, 0.06), this.materials.deadwood, [0, -0.28, 0.78]))
        }
        const carry = new THREE.Group()
        carry.position.set(0, 0.42, 0.85)
        body.add(carry)
        group.userData = { body, arms, legs, carry }
        return group
    }

    createBuilding(_type)
    {
        const group = new THREE.Group()
        group.add(this.createShadow(3.4, 3))
        const movingPart = new THREE.Group()
        const fire = new THREE.Group()
        const smoke = new THREE.Group()
        group.add(movingPart, fire, smoke)
        const crypt = _type.toLowerCase().includes('crypt') || _type === 'lichAltar'
        if(crypt)
        {
            group.add(this.mesh(this.box(2.4, 1.9, 0.25, 0.06), this.materials.graveStone, [0, 0, 0.16]))
            for(const side of [-1, 1])
            {
                group.add(this.mesh(this.box(0.38, 1.7, 1.65, 0.05), this.materials.graveStone, [side * 0.98, 0, 0.98]))
                group.add(this.mesh(new THREE.ConeGeometry(0.28, 0.65, 4), this.materials.graveIron, [side * 0.98, -0.65, 2], [Math.PI * 0.5, 0, Math.PI * 0.25]))
            }
            group.add(this.mesh(this.box(2.25, 1.85, 0.28, 0.05), this.materials.graveIron, [0, 0, 1.83]))
            group.add(this.mesh(this.box(0.85, 0.12, 1.25, 0.04), this.materials.black, [0, -0.9, 0.82]))
            const skull = this.createSkull(0.8)
            skull.position.set(0, -0.98, 1.5)
            group.add(skull)
            movingPart.position.set(0, 0, 2.15)
            movingPart.add(this.createItem(_type === 'lichAltar' ? 'soulCrystal' : 'graveSeal', 1.25))
        }
        else
        {
            group.add(this.mesh(this.cylinder(1.25, 1.45, 0.35, 10), this.materials.graveStone, [0, 0, 0.2]))
            for(let index = 0; index < 6; index++)
            {
                const angle = index / 6 * Math.PI * 2
                group.add(this.mesh(this.box(0.3, 0.3, 1.25, 0.035), this.materials.graveIron, [Math.cos(angle) * 0.92, Math.sin(angle) * 0.92, 0.85], [0, 0, angle]))
            }
            group.add(this.mesh(new THREE.TorusGeometry(0.92, 0.07, 6, 20), this.glowMaterial, [0, 0, 0.48]))
            if(_type === 'graveForge')
            {
                group.add(this.mesh(this.cylinder(0.75, 0.95, 1.2, 10), this.materials.graveStone, [0, 0, 0.95]))
                group.add(this.mesh(this.cylinder(0.55, 0.55, 0.12, 10), this.materials.black, [0, 0, 1.55]))
            }
            else if(_type === 'soulCrucible')
            {
                group.add(this.mesh(new THREE.SphereGeometry(0.75, 12, 8), this.materials.graveIron, [0, 0, 1]))
                movingPart.add(this.createItem('soulCrystal', 2))
                movingPart.position.z = 1.35
            }
            else if(_type === 'sealAltar')
            {
                movingPart.add(this.createItem('graveSeal', 2.2))
                movingPart.position.z = 1.25
            }
            else
            {
                movingPart.add(this.createItem('spiritBinding', 2.2))
                movingPart.position.z = 1.15
            }
        }
        for(let index = 0; index < 4; index++)
        {
            const flame = this.mesh(new THREE.OctahedronGeometry(0.18 + index * 0.025, 0), this.glowMaterial, [(index - 1.5) * 0.28, 0, crypt ? 2.2 : 1.45])
            flame.userData.offset = index * 0.7
            fire.add(flame)
        }
        for(let index = 0; index < 3; index++)
        {
            const puff = this.mesh(new THREE.DodecahedronGeometry(0.13 + index * 0.04, 0), new THREE.MeshBasicMaterial({ color: '#54c98e', transparent: true, opacity: 0.25, depthWrite: false }), [0.25, 0.1, 1.8 + index * 0.25])
            puff.userData.offset = index * 1.7
            smoke.add(puff)
        }
        const input = new THREE.Group()
        const fuel = new THREE.Group()
        const output = new THREE.Group()
        input.position.set(-1.45, 0.15, 0.08)
        fuel.position.set(1.45, 0.15, 0.08)
        output.position.set(0, -1.55, 0.08)
        group.add(input, fuel, output)
        const fuelStatus = new THREE.Group()
        fuelStatus.position.set(0, 0, 3)
        const exclamationMaterial = new THREE.MeshBasicMaterial({ color: '#8affb7', transparent: true, depthTest: false })
        fuelStatus.add(this.mesh(this.box(0.13, 0.13, 0.45, 0.03), exclamationMaterial, [-0.25, 0, 0.3]))
        fuelStatus.add(this.mesh(new THREE.SphereGeometry(0.1, 8, 6), exclamationMaterial, [-0.25, 0, -0.03]))
        const soul = this.createItem('souls', 1.3)
        soul.position.x = 0.3
        fuelStatus.add(soul)
        fuelStatus.visible = false
        group.add(fuelStatus)
        fire.visible = false
        smoke.visible = false
        group.userData = { movingPart, fire, smoke, input, fuel, output, fuelStatus, exclamationMaterial }
        return group
    }

    createOssuary()
    {
        const group = this.createBuilding('guardCrypt')
        const contents = new THREE.Group()
        contents.position.set(0, -0.2, 0.4)
        group.add(contents)
        group.userData.contents = contents
        return group
    }

    createArena()
    {
        const group = new THREE.Group()
        const floor = this.mesh(new THREE.CircleGeometry(10, 40), new THREE.MeshBasicMaterial({ color: '#17131f', transparent: true, opacity: 0.72 }), [ARENA.center.x, ARENA.center.y, 0.015])
        group.add(floor)
        group.add(this.mesh(new THREE.TorusGeometry(9.2, 0.08, 5, 40), this.glowMaterial, [ARENA.center.x, ARENA.center.y, 0.04]))
        for(let index = 0; index < 10; index++)
        {
            const angle = index / 10 * Math.PI * 2
            const height = index % 3 === 0 ? 2.3 : 1.2
            group.add(this.mesh(this.box(0.55, 0.55, height, 0.05), this.materials.graveStone, [Math.cos(angle) * 8.7, ARENA.center.y + Math.sin(angle) * 8.7, height * 0.5], [0.1, 0.1, angle]))
        }
        const portal = new THREE.Group()
        portal.position.set(ARENA.portal.x, ARENA.portal.y, 1.6)
        portal.add(this.mesh(new THREE.TorusGeometry(1.4, 0.18, 8, 24), this.materials.graveIron, [0, 0, 0], [Math.PI * 0.5, 0, 0]))
        portal.add(this.mesh(new THREE.CircleGeometry(1.25, 24), this.glowMaterial, [0, 0.03, 0], [Math.PI * 0.5, 0, 0]))
        group.add(portal)
        group.userData.portal = portal
        return group
    }

    createObelisk()
    {
        const group = new THREE.Group()
        group.position.set(ARENA.obelisk.x, ARENA.obelisk.y, 0)
        group.add(this.mesh(this.box(2, 2, 0.35, 0.05), this.materials.graveStone, [0, 0, 0.18]))
        group.add(this.mesh(new THREE.ConeGeometry(0.75, 4.2, 4), this.materials.graveIron, [0, 0, 2.25], [Math.PI * 0.5, 0, Math.PI * 0.25]))
        const glow = this.mesh(new THREE.TorusGeometry(0.85, 0.07, 6, 20), this.glowMaterial, [0, 0, 1.25])
        group.add(glow)
        group.userData.glow = glow
        return group
    }

    createPin()
    {
        const group = new THREE.Group()
        const haloMaterial = this.glowMaterial.clone()
        haloMaterial.opacity = 0.55
        const halo = this.mesh(new THREE.TorusGeometry(0.48, 0.045, 6, 20), haloMaterial)
        const skull = this.createSkull(0.9)
        skull.position.z = 0.15
        group.add(halo, skull)
        group.userData.halo = halo
        group.userData.haloMaterial = haloMaterial
        return group
    }
}
